// This component provides a filter dropdown for the map, allowing users to filter samples by location, date range, and substance.
// It uses controlled components to manage the filter state and updates the parent component's filter state on change.
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";
import FormButton from "react-bootstrap/Button";
import { useParamsContext, useAdminContext } from "../../contexts";
import Select from "react-select";

import { useEffect, useState } from "react";

const Filter = ({
  setFilter,
  filter,
  sampleData,
  userLocations,
  setActiveFeature,
  defaultFilterState,
  userRole,
}) => {
  const { getColour, getTexture, getAnalyteName } = useParamsContext();
  const { customers, selectedCustomer } = useAdminContext();

  const [filterOptions, setFilterOptions] = useState({});

  const handleChange = (e, name) => {
    setActiveFeature(null);

    // If the filter is a multi-select, handle it differently
    if (Array.isArray(e)) {
      // If it's a multi-select, we expect an array of selected options
      const selectedValues = e.map((option) => option.value);
      setFilter((prevFilter) => ({
        ...prevFilter,
        [name]: selectedValues,
      }));
      return;
    }
    const { value } = e.target;
    setFilter((prevFilter) => ({
      ...prevFilter,
      [name]: value,
    }));
  };

  // get options for filters based on which parameters are present in the samples shown on the map.
  useEffect(() => {
    const features = sampleData?.features ?? [];

    const locationOptions = features
      .map((sample) => sample?.properties?.locationId)
      .filter(Boolean);

    const substanceOptions = features
      .flatMap((sample) => sample?.properties?.findings ?? [])
      .map((finding) => (finding === "Other" ? null : finding))
      .filter(Boolean);

    const colourOptions = features
      .map((sample) => (sample?.properties?.colour || "").slice(0, 3))
      .filter(Boolean);

    const textureOptions = features
      .map(
        (sample) =>
          sample?.properties?.texture?.id ?? sample?.properties?.texture
      )
      .filter(Boolean);

    // arrays of unique values
    const uniqueArrays = {
      locations: Array.from(new Set(locationOptions)),
      substances: Array.from(new Set(substanceOptions)),
      colours: Array.from(new Set(colourOptions)),
      textures: Array.from(new Set(textureOptions)),
    };

    setFilterOptions(uniqueArrays);
  }, [sampleData]);

  return (
    <Dropdown>
      <Dropdown.Toggle variant='dark' id='dropdown-basic-button'>
        Filter
      </Dropdown.Toggle>

      <Dropdown.Menu
        className='p-3'
        style={{ backgroundColor: "rgba(236, 236, 236, 0.75)" }}
      >
        <Form className='d-flex flex-column gap-3'>
          {(userLocations?.length > 1 ||
            (selectedCustomer?.locations?.length ?? 0) > 1 ||
            selectedCustomer?.customer === "Admin") && (
            <Form.Group className='mb-1'>
              <Form.Label className='fw-semibold'>Site:</Form.Label>
              <Select
                name='location'
                value={filter.location.map((location) => {
                  if (userRole === "admin") {
                    const customer = customers?.find((cust) =>
                      cust?.locations?.some(
                        (loc) => loc.locationId === location
                      )
                    );
                    const locObj = customer?.locations?.find(
                      (loc) => loc.locationId === location
                    );
                    const label = locObj?.name
                      ? `${customer?.customer ?? "Customer"} - ${locObj.name}`
                      : location;
                    return { value: location, label };
                  }
                  const locObj = userLocations.find(
                    (loc) => loc.locationId === location
                  );
                  return { value: location, label: locObj?.name || location };
                })}
                options={filterOptions.locations?.map((location) => {
                  if (userRole === "admin") {
                    const customer = customers?.find((cust) =>
                      cust?.locations?.some(
                        (loc) => loc.locationId === location
                      )
                    );
                    const locObj = customer?.locations?.find(
                      (loc) => loc.locationId === location
                    );
                    const label = locObj?.name
                      ? `${customer?.customer ?? "Customer"} - ${locObj.name}`
                      : location;
                    return { value: location, label };
                  }
                  const locObj = userLocations.find(
                    (loc) => loc.locationId === location
                  );
                  return { value: location, label: locObj?.name || location };
                })}
                isClearable
                isMulti
                className='basic-multi-select'
                classNamePrefix='select'
                onChange={(e) => {
                  handleChange(e, "location");
                }}
              ></Select>
            </Form.Group>
          )}

          <Form.Group className='mb-1'>
            <Form.Label className='fw-semibold'>Substance:</Form.Label>
            <Select
              name='substance'
              value={filter.substance.map((substance) => ({
                value: substance,
                label: getAnalyteName(substance),
              }))}
              options={filterOptions.substances?.map((substance) => ({
                value: substance,
                label: getAnalyteName(substance),
              }))}
              isClearable
              isMulti
              className='basic-multi-select'
              classNamePrefix='select'
              onChange={(e) => {
                handleChange(e, "substance");
              }}
            ></Select>
          </Form.Group>

          <Form.Group className='mb-1'>
            <Form.Label className='fw-semibold'>Colour:</Form.Label>
            <Select
              name='colour'
              value={filter.colour.map((colour) => ({
                value: colour,
                label: getColour(colour)?.label || colour,
              }))}
              options={filterOptions.colours?.map((colour) => ({
                value: colour,
                label: getColour(colour)?.label || colour,
              }))}
              isClearable
              isMulti
              className='basic-multi-select'
              classNamePrefix='select'
              onChange={(e) => {
                handleChange(e, "colour");
              }}
            ></Select>
          </Form.Group>

          <Form.Group className='mb-1'>
            <Form.Label className='fw-semibold'>Texture:</Form.Label>
            <Select
              name='texture'
              value={filter.texture.map((texture) => ({
                value: texture,
                label: getTexture({ id: texture })?.label || texture,
              }))}
              options={filterOptions.textures?.map((texture) => ({
                value: texture,
                label: getTexture({ id: texture })?.label || texture,
              }))}
              isClearable
              isMulti
              className='basic-multi-select'
              classNamePrefix='select'
              onChange={(e) => {
                handleChange(e, "texture");
              }}
            ></Select>
          </Form.Group>

          <Form.Group>
            <Form.Label className='fw-semibold'>Date Range:</Form.Label>
            <div className='d-flex gap-2'>
              <Form.Control
                type='date'
                name='startDate'
                value={filter.startDate}
                onChange={(e) => {
                  handleChange(e, "startDate");
                }}
              />
              to
              <Form.Control
                type='date'
                name='endDate'
                value={filter.endDate}
                onChange={(e) => {
                  handleChange(e, "endDate");
                }}
              />
            </div>
          </Form.Group>
          <FormButton
            variant='primary'
            onClick={() => {
              setFilter(defaultFilterState);
              setActiveFeature(null);
            }}
          >
            Reset
          </FormButton>
        </Form>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default Filter;
